/**
 * Shared finalisation parameters per client/project.
 *
 * Source of truth:
 * - server (`/v1/clients/:clientId/finalisation-params`)
 * - React Query cache for in-app sharing
 *
 * localStorage is only used as a one-shot bootstrap for legacy data.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { clientDataKeys } from '@/services/clientDataService';
import type { PricingMode } from '@/utils/packAilixPricing';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FinalisationParams {
  nbDevelopers: number;
  offerMode: PricingMode;
  startMonth: number;
  startYear: number;
  enabledPrestations: Record<string, boolean>;
}

interface FinalisationParamsQueryData {
  isInitialized: boolean;
  params: FinalisationParams;
}

// ---------------------------------------------------------------------------
// Constants / module state
// ---------------------------------------------------------------------------

const VALID_OFFER_MODES: PricingMode[] = ['a_la_carte', 'pack_ailix_1an', 'pack_ailix_2ans'];
const FINALISATION_PARAMS_STALE_TIME_MS = 0;
const FINALISATION_PARAMS_GC_TIME_MS = 10 * 60 * 1000;
const FINALISATION_PARAMS_POLL_INTERVAL_MS = 5000;

const _finalisationSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
const _finalisationPollTimers = new Map<string, ReturnType<typeof setInterval>>();
const _finalisationInFlightRefetches = new Map<string, Promise<unknown>>();
const _finalisationSyncVersions = new Map<string, number>();
const _finalisationConfirmedState = new Map<string, FinalisationParamsQueryData>();
const _finalisationBootstrapAttempted = new Set<string>();
const _finalisationDirtyClients = new Set<string>();
const _finalisationRefetchListeners = new Map<string, Set<() => void>>();
let _finalisationVisibilityListenerRegistered = false;

export const finalisationParamsKeys = {
  root: ['finalisationParams'] as const,
  byClient: (clientId: string) => ['finalisationParams', clientId] as const,
};

function notifyFinalisationParamsRefetch(clientId: string) {
  const listeners = _finalisationRefetchListeners.get(clientId);
  if (!listeners?.size) return;
  listeners.forEach((listener) => listener());
}

function startFinalisationParamsPolling(clientId: string) {
  if (_finalisationPollTimers.has(clientId) || typeof window === 'undefined') return;

  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    notifyFinalisationParamsRefetch(clientId);
  }, FINALISATION_PARAMS_POLL_INTERVAL_MS);

  _finalisationPollTimers.set(clientId, timer);
}

function stopFinalisationParamsPolling(clientId: string) {
  const timer = _finalisationPollTimers.get(clientId);
  if (!timer) return;

  clearInterval(timer);
  _finalisationPollTimers.delete(clientId);
}

function registerFinalisationParamsVisibilityListener() {
  if (_finalisationVisibilityListenerRegistered || typeof document === 'undefined') return;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    _finalisationRefetchListeners.forEach((_listeners, clientId) => {
      notifyFinalisationParamsRefetch(clientId);
    });
  });

  _finalisationVisibilityListenerRegistered = true;
}

function registerFinalisationParamsRefetch(clientId: string, refetch: () => void): () => void {
  registerFinalisationParamsVisibilityListener();

  const listeners = _finalisationRefetchListeners.get(clientId) ?? new Set<() => void>();
  listeners.add(refetch);
  _finalisationRefetchListeners.set(clientId, listeners);
  startFinalisationParamsPolling(clientId);

  return () => {
    const current = _finalisationRefetchListeners.get(clientId);
    if (!current) return;

    current.delete(refetch);
    if (current.size === 0) {
      _finalisationRefetchListeners.delete(clientId);
      stopFinalisationParamsPolling(clientId);
    }
  };
}

function runSharedFinalisationParamsRefetch(
  clientId: string,
  refetch: () => Promise<unknown> | unknown
): Promise<unknown> {
  const existing = _finalisationInFlightRefetches.get(clientId);
  if (existing) return existing;

  const request = Promise.resolve(refetch()).finally(() => {
    if (_finalisationInFlightRefetches.get(clientId) === request) {
      _finalisationInFlightRefetches.delete(clientId);
    }
  });

  _finalisationInFlightRefetches.set(clientId, request);
  return request;
}

// ---------------------------------------------------------------------------
// Defaults / normalization
// ---------------------------------------------------------------------------

/**
 * Default parameters: 2 developers, a_la_carte, starting next month.
 */
function getDefaultParams(): FinalisationParams {
  const now = new Date();
  const nextMonth = now.getMonth() + 2;
  return {
    nbDevelopers: 2,
    offerMode: 'a_la_carte',
    startMonth: nextMonth > 12 ? 1 : nextMonth,
    startYear: nextMonth > 12 ? now.getFullYear() + 1 : now.getFullYear(),
    enabledPrestations: {},
  };
}

function normalizeEnabledPrestations(input: unknown): Record<string, boolean> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  return Object.entries(input as Record<string, unknown>).reduce<Record<string, boolean>>((acc, [key, value]) => {
    if (typeof key === 'string' && typeof value === 'boolean') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function normalizeFinalisationParams(input: Partial<FinalisationParams> | null | undefined): FinalisationParams {
  const defaults = getDefaultParams();
  const candidate = input ?? {};

  return {
    nbDevelopers:
      typeof candidate.nbDevelopers === 'number' && isFinite(candidate.nbDevelopers)
        ? Math.max(0.5, Math.min(10, Math.round(candidate.nbDevelopers * 10) / 10))
        : defaults.nbDevelopers,
    offerMode: VALID_OFFER_MODES.includes(candidate.offerMode as PricingMode)
      ? (candidate.offerMode as PricingMode)
      : defaults.offerMode,
    startMonth:
      typeof candidate.startMonth === 'number' && candidate.startMonth >= 1 && candidate.startMonth <= 12
        ? candidate.startMonth
        : defaults.startMonth,
    startYear:
      typeof candidate.startYear === 'number' && candidate.startYear >= 2024
        ? candidate.startYear
        : defaults.startYear,
    enabledPrestations: normalizeEnabledPrestations(candidate.enabledPrestations),
  };
}

function areFinalisationParamsEqual(a: FinalisationParams, b: FinalisationParams): boolean {
  const aKeys = Object.keys(a.enabledPrestations);
  const bKeys = Object.keys(b.enabledPrestations);
  if (
    a.nbDevelopers !== b.nbDevelopers ||
    a.offerMode !== b.offerMode ||
    a.startMonth !== b.startMonth ||
    a.startYear !== b.startYear ||
    aKeys.length !== bKeys.length
  ) {
    return false;
  }

  return aKeys.every((key) => a.enabledPrestations[key] === b.enabledPrestations[key]);
}

function buildDefaultQueryData(): FinalisationParamsQueryData {
  return {
    isInitialized: false,
    params: getDefaultParams(),
  };
}

function mapApiDataToQueryData(apiData: Record<string, unknown> | null | undefined): FinalisationParamsQueryData {
  const isInitialized = apiData?.is_initialized === true;
  if (!isInitialized) {
    return buildDefaultQueryData();
  }

  return {
    isInitialized,
    params: normalizeFinalisationParams({
      nbDevelopers: typeof apiData?.nb_developers === 'number' ? apiData.nb_developers : Number(apiData?.nb_developers),
      offerMode: apiData?.offer_mode as PricingMode,
      startMonth: typeof apiData?.start_month === 'number' ? apiData.start_month : Number(apiData?.start_month),
      startYear: typeof apiData?.start_year === 'number' ? apiData.start_year : Number(apiData?.start_year),
      enabledPrestations: apiData?.enabled_prestations,
    }),
  };
}

function buildApiPayload(params: FinalisationParams) {
  return {
    nb_developers: params.nbDevelopers,
    start_month: params.startMonth,
    start_year: params.startYear,
    offer_mode: params.offerMode,
    enabled_prestations: params.enabledPrestations,
  };
}

// ---------------------------------------------------------------------------
// Legacy bootstrap
// ---------------------------------------------------------------------------

function readLegacyFinalisationParams(clientId: string): FinalisationParams | null {
  if (typeof window === 'undefined' || !clientId) return null;

  let foundLegacyValue = false;
  let params = getDefaultParams();

  try {
    const rawLegacyParams = localStorage.getItem(`finalisationParams_${clientId}`);
    if (rawLegacyParams !== null) {
      foundLegacyValue = true;
      try {
        const parsed = JSON.parse(rawLegacyParams);
        params = normalizeFinalisationParams(parsed as Partial<FinalisationParams>);
      } catch {
        // Ignore malformed legacy payloads and keep defaults.
      }
    }

    const rawLegacyOfferMode = localStorage.getItem(`packAilixMode_${clientId}`);
    if (rawLegacyOfferMode !== null) {
      foundLegacyValue = true;
      if (VALID_OFFER_MODES.includes(rawLegacyOfferMode as PricingMode)) {
        params = {
          ...params,
          offerMode: rawLegacyOfferMode as PricingMode,
        };
      }
    }
  } catch {
    return null;
  }

  return foundLegacyValue ? params : null;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchFinalisationParams(clientId: string, signal?: AbortSignal): Promise<FinalisationParamsQueryData> {
  const response = await apiFetch(`/v1/clients/${clientId}/finalisation-params`, { signal });
  return mapApiDataToQueryData(response as Record<string, unknown>);
}

async function saveFinalisationParams(clientId: string, params: FinalisationParams): Promise<FinalisationParamsQueryData> {
  const response = await apiFetch(`/v1/clients/${clientId}/finalisation-params`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(buildApiPayload(params)),
  });
  const mapped = mapApiDataToQueryData(response as Record<string, unknown>);
  _finalisationConfirmedState.set(clientId, mapped);
  return mapped;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFinalisationParams(clientId: string) {
  const queryClient = useQueryClient();
  const queryKey = clientId ? finalisationParamsKeys.byClient(clientId) : finalisationParamsKeys.root;

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      const startedVersion = _finalisationSyncVersions.get(clientId) ?? 0;

      return fetchFinalisationParams(clientId, signal).then((mapped) => {
        const currentVersion = _finalisationSyncVersions.get(clientId) ?? 0;
        const currentData = queryClient.getQueryData<FinalisationParamsQueryData>(queryKey);
        if ((_finalisationDirtyClients.has(clientId) || currentVersion !== startedVersion) && currentData) {
          return currentData;
        }

        _finalisationDirtyClients.delete(clientId);
        _finalisationConfirmedState.set(clientId, mapped);
        return mapped;
      });
    },
    enabled: Boolean(clientId),
    staleTime: FINALISATION_PARAMS_STALE_TIME_MS,
    gcTime: FINALISATION_PARAMS_GC_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const collaborativeRefetch = useCallback(
    () => queryClient.refetchQueries({ queryKey, exact: true, type: 'active' }),
    [queryClient, queryKey]
  );

  useEffect(() => {
    if (!clientId) return;

    return registerFinalisationParamsRefetch(clientId, () => {
      void runSharedFinalisationParamsRefetch(clientId, collaborativeRefetch);
    });
  }, [clientId, collaborativeRefetch]);

  const params = useMemo(
    () => normalizeFinalisationParams(query.data?.params),
    [query.data?.params]
  );

  const scheduleSync = useCallback((nextParams: FinalisationParams) => {
    if (!clientId) return;

    const nextVersion = (_finalisationSyncVersions.get(clientId) ?? 0) + 1;
    _finalisationSyncVersions.set(clientId, nextVersion);

    const existing = _finalisationSyncTimers.get(clientId);
    if (existing) clearTimeout(existing);

    _finalisationSyncTimers.set(clientId, setTimeout(async () => {
      _finalisationSyncTimers.delete(clientId);

      try {
        const saved = await saveFinalisationParams(clientId, nextParams);
        if ((_finalisationSyncVersions.get(clientId) ?? 0) !== nextVersion) return;

        _finalisationDirtyClients.delete(clientId);
        queryClient.setQueryData(queryKey, saved);
        notifyFinalisationParamsRefetch(clientId);
        void queryClient.invalidateQueries({ queryKey: clientDataKeys.byClientRoot(clientId) });
      } catch {
        if ((_finalisationSyncVersions.get(clientId) ?? 0) !== nextVersion) return;

        _finalisationDirtyClients.delete(clientId);
      }
    }, 1000));
  }, [clientId, queryClient, queryKey]);

  useEffect(() => {
    if (!clientId || !query.data || query.data.isInitialized || _finalisationBootstrapAttempted.has(clientId)) {
      return;
    }

    _finalisationBootstrapAttempted.add(clientId);
    const legacyParams = readLegacyFinalisationParams(clientId);
    if (!legacyParams) return;

    const bootstrapVersion = (_finalisationSyncVersions.get(clientId) ?? 0) + 1;
    _finalisationSyncVersions.set(clientId, bootstrapVersion);
    _finalisationDirtyClients.add(clientId);

    const optimisticBootstrap = {
      isInitialized: true,
      params: legacyParams,
    };

    void queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, optimisticBootstrap);

    void saveFinalisationParams(clientId, legacyParams)
      .then((saved) => {
        if ((_finalisationSyncVersions.get(clientId) ?? 0) !== bootstrapVersion) return;

        _finalisationDirtyClients.delete(clientId);
        queryClient.setQueryData(queryKey, saved);
        notifyFinalisationParamsRefetch(clientId);
        void queryClient.invalidateQueries({ queryKey: clientDataKeys.byClientRoot(clientId) });
      })
      .catch(() => {
        if ((_finalisationSyncVersions.get(clientId) ?? 0) !== bootstrapVersion) return;

        _finalisationDirtyClients.delete(clientId);
      });
  }, [clientId, query.data, queryClient, queryKey]);

  const setParams = useCallback((update: Partial<FinalisationParams>) => {
    if (!clientId) return;

    void queryClient.cancelQueries({ queryKey });

    let nextData: FinalisationParamsQueryData | null = null;
    queryClient.setQueryData<FinalisationParamsQueryData>(queryKey, (prev) => {
      const previous = normalizeFinalisationParams(prev?.params);
      const nextParams = normalizeFinalisationParams({ ...previous, ...update });

      if (areFinalisationParamsEqual(previous, nextParams)) {
        nextData = prev ?? { isInitialized: true, params: previous };
        return prev ?? { isInitialized: true, params: previous };
      }

      _finalisationDirtyClients.add(clientId);
      nextData = {
        isInitialized: true,
        params: nextParams,
      };
      return nextData;
    });

    if (!nextData) return;
    scheduleSync(nextData.params);
  }, [clientId, queryClient, queryKey, scheduleSync]);

  return {
    params,
    setParams,
  };
}
